import { Link } from '@tanstack/react-router';
import { ExternalLink, Check, Clock, AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/Button';

export interface AlertData {
  id: string;
  pond_id: string;
  pond_name?: string;
  cycle_id?: string;
  alert_type: string;
  severity: 'info' | 'warning' | 'critical';
  status: 'active' | 'acknowledged' | 'resolved';
  message: string;
  triggered_value?: number;
  threshold_value?: number;
  triggered_at: string;
  acknowledged_at?: string;
  acknowledgement_notes?: string;
}

interface AlertCardProps {
  alert: AlertData;
  onAcknowledge?: () => void;
  showAcknowledgeButton?: boolean;
}

const severityConfig = {
  critical: {
    borderColor: 'border-l-red-500',
    bgColor: 'bg-red-50/50',
    icon: AlertCircle,
    iconColor: 'text-red-600',
    badge: 'bg-red-100 text-red-700 ring-red-600/20',
  },
  warning: {
    borderColor: 'border-l-amber-400',
    bgColor: 'bg-amber-50/30',
    icon: AlertTriangle,
    iconColor: 'text-amber-600',
    badge: 'bg-amber-100 text-amber-700 ring-amber-600/20',
  },
  info: {
    borderColor: 'border-l-blue-400',
    bgColor: 'bg-blue-50/30',
    icon: Info,
    iconColor: 'text-blue-600',
    badge: 'bg-blue-100 text-blue-700 ring-blue-600/20',
  },
};

function formatAlertType(type: string): string {
  const labels: Record<string, string> = {
    low_do: 'Low DO',
    high_do: 'High DO',
    high_ph: 'High pH',
    low_ph: 'Low pH',
    high_ammonia: 'High Ammonia',
    high_nitrite: 'High Nitrite',
    ph_dial_high: 'pH Swing High',
    low_temp: 'Low Temp',
    high_temp: 'High Temp',
    low_salinity: 'Low Salinity',
    high_salinity: 'High Salinity',
    low_growth: 'Low Growth',
    low_survival: 'Low Survival',
    high_fcr: 'High FCR',
  };
  return labels[type] || type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function AlertCard({ alert, onAcknowledge, showAcknowledgeButton = true }: AlertCardProps) {
  const config = severityConfig[alert.severity];
  const Icon = config.icon;
  const timeAgo = formatDistanceToNow(new Date(alert.triggered_at), { addSuffix: true });

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-lg border border-gray-200/80',
        'bg-white shadow-sm',
        'border-l-4',
        config.borderColor,
        'transition-all duration-200 hover:shadow-md',
        'group'
      )}
    >
      {/* Subtle severity background overlay */}
      <div className={cn('absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity', config.bgColor)} />

      <div className="relative flex items-start gap-4 p-4">
        {/* Severity Icon */}
        <div className={cn('shrink-0 mt-0.5')}>
          <div className={cn(
            'w-9 h-9 rounded-full flex items-center justify-center',
            'bg-white ring-2 ring-gray-100 shadow-sm'
          )}>
            <Icon className={cn('w-5 h-5', config.iconColor)} />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header Row */}
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <Link
              to="/ponds/$pondId"
              params={{ pondId: alert.pond_id }}
              className="font-semibold text-gray-900 hover:text-primary-600 transition-colors"
            >
              {alert.pond_name || 'Unknown Pond'}
            </Link>
            <span className="text-gray-300">•</span>
            <span className={cn(
              'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset',
              config.badge
            )}>
              {formatAlertType(alert.alert_type)}
            </span>
          </div>

          {/* Message */}
          <p className="text-gray-700 text-sm leading-relaxed mb-2">{alert.message}</p>

          {/* Metadata Row */}
          <div className="flex items-center gap-4 text-xs text-gray-500">
            {alert.triggered_value !== undefined && alert.threshold_value !== undefined && (
              <span className="flex items-center gap-1.5">
                <span className="font-mono font-medium text-gray-700">{alert.triggered_value}</span>
                <span className="text-gray-400">/</span>
                <span className="text-gray-500">{alert.threshold_value} threshold</span>
              </span>
            )}
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              {timeAgo}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0">
          <Link
            to="/ponds/$pondId"
            params={{ pondId: alert.pond_id }}
            className={cn(
              'p-2 rounded-lg text-gray-400 hover:text-gray-600',
              'hover:bg-gray-100 transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-primary-500/20'
            )}
            title="View pond details"
          >
            <ExternalLink className="w-4 h-4" />
          </Link>

          {showAcknowledgeButton && alert.status === 'active' && onAcknowledge && (
            <Button
              size="sm"
              variant="outline"
              onClick={onAcknowledge}
              leftIcon={<Check className="w-3.5 h-3.5" />}
              className="shadow-none"
            >
              Ack
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
